import { AdditionalInterestType } from "@covertree/new-business-graphql";

import {
  AdditionalInterestFormValues,
  cloneStateOption,
  toOptionType,
} from "./lenderAutofill";
import { getLenderName } from "./lenders";

export type AdditionalInterestFormMode = "add" | "edit" | "replace";

/** One additional interest as the form holds it: optionals flattened to "". */
export interface AdditionalInterestFormData {
  name: string;
  /** null while the type select still shows its placeholder. */
  type: AdditionalInterestType | "" | null;
  streetAddress: string;
  lotUnit: string;
  city: string;
  state: { label: string; value: string };
  zipCode: string;
  email: string;
  accountNumber: string;
  isBilling?: boolean;
}

export const defaultAdditionalInterestFormData: AdditionalInterestFormData = {
  name: "",
  type: null,
  streetAddress: "",
  lotUnit: "",
  city: "",
  state: { label: "", value: "" },
  zipCode: "",
  email: "",
  accountNumber: "",
};

/** Accepted when seeding the form - also covers the checkout storage shape. */
export interface AdditionalInterestSource {
  name: string;
  type: AdditionalInterestType | "" | null;
  streetAddress: string;
  lotUnit?: string | null;
  city: string;
  state: { label?: string | null; value?: string | null };
  zipCode: string;
  email?: string | null;
  accountNumber?: string | null;
  isBilling?: boolean;
}

/**
 * A tenant types the name freely, everyone else picks it from the lender
 * autocomplete, which holds an option object rather than a string.
 */
export const toInterestFormValues = (
  data: AdditionalInterestSource,
  isTenantPolicy = false
): AdditionalInterestFormValues => ({
  ...data,
  name: isTenantPolicy ? data.name : toOptionType(data.name),
  state: cloneStateOption(data.state),
  lotUnit: data.lotUnit ?? "",
  email: data.email ?? "",
  accountNumber: data.accountNumber ?? "",
});

/**
 * Lender autofill copies zips as stored in the lender list, where some carry a
 * dash or lost a leading zero. Reduce to the 5 or 9 digits the backend expects,
 * dropping a truncated plus-four rather than guessing it.
 */
export const normalizeZipCode = (zip: string) => {
  const [base = "", plus4] = zip
    .split("-")
    .map((part) => part.replace(/\D/g, ""));

  if (!base) return "";

  if (plus4 !== undefined) {
    return plus4.length === 4
      ? base.padStart(5, "0") + plus4
      : base.padStart(5, "0");
  }

  return base.padStart(base.length > 5 ? 9 : 5, "0");
};

export const normalizeInterestName = (
  name: AdditionalInterestFormValues["name"]
): string => {
  if (!name) return "";
  if (typeof name === "string") return getLenderName(name.trim());
  return name.value ? getLenderName(name.value.trim()) : "";
};

export const toInterestFormData = (
  values: AdditionalInterestFormValues
): AdditionalInterestFormData => {
  const { name, state, ...rest } = values;

  return {
    ...rest,
    type: (rest.type ?? "") as AdditionalInterestType | "",
    name: normalizeInterestName(name),
    zipCode: normalizeZipCode(rest.zipCode),
    state: cloneStateOption(state),
    lotUnit: rest.lotUnit ?? "",
    email: rest.email ?? "",
    accountNumber: rest.accountNumber ?? "",
  };
};
